'use no memo';

import React from 'react';
import { RouteObject, useRoutes } from 'react-router';

import { LoadingSpinner } from '@local/loading-indicators';
import { safeLazy } from '@local/utils';

import { ThemeRouteSync } from 'context/ThemeContext';
import useAssessFeatureFlag, { FLAG_NAME } from 'hooks/useAssessFeatureFlag';

import ApplePayContext, {
  ApplePayContextProps,
} from './context/ApplePayContext';

const YelpRestaurantRedirect = safeLazy(
  () => import('./components/YelpRestaurantRedirect'),
  <LoadingSpinner />
);

const Locations = safeLazy(
  () => import('./containers/Locations'),
  <LoadingSpinner />
);

const Restaurant = safeLazy(
  () => import('./pages/RestaurantPage'),
  <LoadingSpinner />
);
const CateringRedirect = safeLazy(
  () => import('./components/CateringRedirect'),
  <LoadingSpinner />
);
const CateringInquiry = safeLazy(
  () => import('./containers/CateringInquiry'),
  <LoadingSpinner />
);
const CateringInquiryConfirmation = safeLazy(
  () => import('./containers/CateringInquiryConfirmation'),
  <LoadingSpinner />
);
const CheckoutContainer = safeLazy(
  () => import('./containers/Checkout/CheckoutContainer'),
  <LoadingSpinner />
);
const Account = safeLazy(
  () => import('./containers/Account'),
  <LoadingSpinner />
);
const OrderHistory = safeLazy(
  () => import('./containers/OrderHistory'),
  <LoadingSpinner />
);
const Confirmation = safeLazy(
  () => import('./containers/Confirmation'),
  <LoadingSpinner />
);
const MembershipPages = safeLazy(
  () => import('./pages/MembershipPages'),
  <LoadingSpinner />
);
const Forgot = safeLazy(
  // @ts-expect-error Forgot component returns void for redirect-only behavior
  () => import('./containers/Forgot'),
  <LoadingSpinner />
);
const Support = safeLazy(
  () => import('./pages/SupportPage/SupportPage'),
  <LoadingSpinner />
);
const NotFound = safeLazy(
  () => import('./pages/NotFoundPage/NotFoundPage'),
  <LoadingSpinner />
);

type AppRoutesProps = {
  applePayContextProps: ApplePayContextProps;
};

const AppRoutes: React.FunctionComponent<AppRoutesProps> = ({
  applePayContextProps,
}) => {
  const isCateringEnabled = !!useAssessFeatureFlag(FLAG_NAME.flagCatering);
  const isCateringInquiryFormEnabled = !!useAssessFeatureFlag(
    FLAG_NAME.flagCateringInquiryForm
  );

  const routeObjects = React.useMemo<RouteObject[]>(
    () =>
      getAppRoutes(
        isCateringEnabled,
        isCateringInquiryFormEnabled,
        applePayContextProps
      ),
    [isCateringEnabled, isCateringInquiryFormEnabled, applePayContextProps]
  );

  return useRoutes(routeObjects);
};

/**
 * Get an array of the application routes
 * @param isCateringEnabled
 * @param isCateringInquiryFormEnabled
 * @param applePayContextProps
 */
export const getAppRoutes = (
  isCateringEnabled: boolean,
  isCateringInquiryFormEnabled: boolean,
  applePayContextProps: ApplePayContextProps
): RouteObject[] => {
  // canonical urls are set in cloudfront
  // '/dine-in' and '/catering' canonicals are set to '/order'
  // if new paths are added, ensure the canonical logic in cloudfront handles the new route as expected for SEO
  const routes: RouteObject[] = [
    {
      path: '/yelp/order/location/:restaurantId',
      element: <YelpRestaurantRedirect />,
    },
    {
      path: '/order/:hqId/locations',
      element: <Locations />,
    },
    {
      path: '/dine-in/:hqId/locations',
      element: <Locations />,
    },
    {
      path: '/order/:hqId/locations/:restaurantId',
      element: <Restaurant />,
    },
    {
      path: '/dine-in/:hqId/locations/:restaurantId',
      element: <Restaurant />,
    },
    {
      path: '/order/:hqId/locations/:restaurantId/checkout',
      element: (
        <ApplePayContext.Provider value={applePayContextProps}>
          <CheckoutContainer />
        </ApplePayContext.Provider>
      ),
    },
    {
      path: '/dine-in/:hqId/locations/:restaurantId/checkout',
      element: (
        <ApplePayContext.Provider value={applePayContextProps}>
          <CheckoutContainer />
        </ApplePayContext.Provider>
      ),
    },
    // Marketplace needs to send requests prepended with '/order' to invoke our cloud function and pull the Order Direct bundles
    {
      path: '/account',
      element: <Account />,
    },
    {
      path: '/order/account',
      element: <Account />,
    },
    {
      path: '/order/history',
      element: <OrderHistory />,
    },
    {
      path: '/dine-in/history',
      element: <OrderHistory />,
    },
    {
      path: '/order/history/:orderId',
      element: <Confirmation />,
    },
    {
      path: '/dine-in/history/:orderId',
      element: <Confirmation />,
    },
    // Membership Pages
    {
      path: '/order/:hqId/locations/:restaurantId/membership/*',
      element: <MembershipPages />,
    },
    {
      path: '/dine-in/:hqId/locations/:restaurantId/membership/*',
      element: <MembershipPages />,
    },
    // Forgot page when coming from forgot password email
    {
      path: '/order/forgot',
      element: <Forgot />,
    },
    {
      path: '/order/:hqId/support',
      element: <Support />,
    },
    {
      path: '/dine-in/:hqId/support',
      element: <Support />,
    },
    {
      path: '*',
      element: <NotFound />,
    },
  ];

  const cateringRoutes: RouteObject[] = isCateringEnabled
    ? [
        {
          path: '/catering/:hqId/locations',
          element: <Locations />,
        },
        {
          path: '/catering/:hqId/locations/:restaurantId',
          element: <CateringRedirect />,
        },
        {
          path: '/catering/:hqId/locations/:restaurantId/checkout',
          element: (
            <ApplePayContext.Provider value={applePayContextProps}>
              <CheckoutContainer />
            </ApplePayContext.Provider>
          ),
        },
        {
          path: '/catering/history',
          element: <OrderHistory />,
        },
        {
          path: '/catering/history/:orderId',
          element: <Confirmation />,
        },
        {
          path: '/catering/:hqId/support',
          element: <Support />,
        },
      ]
    : [];

  const cateringInquiryRoutes = isCateringInquiryFormEnabled
    ? [
        {
          path: '/catering/:hqId/locations/:restaurantId/inquiry',
          element: <CateringInquiry />,
        },
        {
          path: '/catering/:hqId/locations/:restaurantId/inquiry/confirmation',
          element: <CateringInquiryConfirmation />,
        },
      ]
    : [];

  return [
    {
      element: <ThemeRouteSync />,
      children: [...routes, ...cateringRoutes, ...cateringInquiryRoutes],
    },
  ];
};

export default AppRoutes;
